/* ──────────────────────────────────────────────────────────────────────
 *  Dimension advance-action config (DEV-1306 prototype)
 *
 *  Drives the six-dimension My Impact panels: each panel shows the active
 *  actionable recommendations for that dimension. Clicking an action opens
 *  the DimensionActionModal (explainer + CTA).
 *
 *  Handler model (pattern 1, Skip 2026-06-15):
 *    - 'route' — the CTA navigates to the real surface where the user does
 *      the action (most actions).
 *    - 'tray'  — the CTA opens an in-place tray and completes without leaving
 *      the board (2FA-style quick actions). Trays land in pass two; until
 *      then the modal shows the CTA as "coming soon."
 *    - 'info'  — explainer only, no destination (e.g. the in-person civic
 *      act, whose verification surface is still pending).
 *
 *  DISABLED (Slice 1, 2026-07-21 — HANDOFF_MYIMPACT_SUGGESTION_REMOVAL):
 *    Every development suggestion here is set to `state: 'soon'`, so the
 *    `DimensionAdvicePanel` render gate (which keeps only `available`
 *    actions) hides them all. These hardcoded acts emit ZERO impact credit
 *    (none maps to an ImpactEmission binding), so showing them beside a real
 *    medal is a truth defect — the exact promise DEV-1480 exists to kill.
 *    Re-enabling is NOT flipping these flags: an act is "perfected" by moving
 *    it into the `impact_activities` catalog (DEV-1480) with a real fact
 *    detector / `emission_binding`, then turning it on via the admin toggle.
 *    The entries stay here (per-suggestion re-flippable) only so the file
 *    documents which acts are pending; they are structural, not live.
 *
 *  NOTE (prototype): strings here are English literals so we can judge the
 *  layout at full width. Before any act is re-surfaced, route every
 *  user-facing string through useAppTranslation + add 8-locale keys per
 *  CLAUDE.md. Medal is already taken live from the Impact Signature tiers.
 * ────────────────────────────────────────────────────────────────────── */

import type { DimKey } from '../../Components/DimensionRadar/dimensionConfig';

export type ActionState = 'available' | 'soon' | 'locked' | 'done';

/** What the action's modal CTA does. */
export type ActionHandler =
  | { kind: 'route'; to: string; cta: string }
  | { kind: 'tray'; tray: string; cta: string }
  | { kind: 'info'; cta?: string };

export interface AdvanceAction {
  label: string;
  /** available (default) = a live next move; soon = feature pending;
      locked = higher rung; done = already completed. */
  state?: ActionState;
  /** Optional trailing note, e.g. "unlocks Gold" or "opens later". */
  note?: string;
  /** One-sentence explainer shown in the action modal. */
  blurb?: string;
  /** What the modal's primary button does. */
  handler?: ActionHandler;
}

export interface DimAdvance {
  title: string;
  subtitle: string;
  actions: AdvanceAction[];
  /** Greyed next-rung hint at the foot of the panel. */
  lockedHint?: string;
}

export const DIMENSION_ADVANCE: Record<DimKey, DimAdvance> = {
  mind: {
    title: 'Mind',
    subtitle: 'Understanding & insight',
    actions: [
      {
        label: 'Write a chronicle',
        state: 'soon',
        blurb: "Document a project's story — what you did, what happened, and what you learned.",
        handler: { kind: 'route', to: '/content/new', cta: 'Open the editor' },
      },
      {
        label: 'Submit research to a Problem',
        state: 'soon',
        blurb: 'Add evidence or analysis to a Problem the community is working on.',
        handler: { kind: 'route', to: '/problems', cta: 'Go to Problems' },
      },
      {
        label: 'Read a knowledge article',
        state: 'soon',
        blurb: "Learn from the community's shared body of knowledge.",
        handler: { kind: 'route', to: '/content', cta: 'Browse Knowledge' },
      },
      {
        label: 'Promote a chronicle to Knowledge',
        state: 'soon',
        blurb: 'Turn a strong chronicle into a reusable knowledge article.',
        handler: { kind: 'route', to: '/content', cta: 'Go to your content' },
      },
    ],
    lockedHint: "Review others' work · Leader",
  },
  voice: {
    title: 'Voice',
    subtitle: 'Awareness & influence',
    actions: [
      {
        label: 'Share a project',
        state: 'soon',
        blurb: "Put a project in front of people who'd care about it.",
        handler: { kind: 'route', to: '/my-projects', cta: 'Go to your projects' },
      },
      {
        label: 'Invite a friend',
        state: 'soon',
        blurb: 'Bring someone new into the mission.',
        handler: { kind: 'tray', tray: 'invite', cta: 'Invite a friend' },
      },
      {
        label: 'Endorse a project',
        state: 'soon',
        blurb: 'Publicly back a project you believe in.',
        handler: { kind: 'route', to: '/discover', cta: 'Find a project' },
      },
      { label: 'Post & comment', state: 'soon', note: 'coming soon' },
    ],
    lockedHint: 'Run a campaign · Leader',
  },
  body: {
    title: 'Body',
    subtitle: 'Action & execution',
    actions: [
      {
        label: 'Volunteer for a project role',
        state: 'soon',
        blurb: 'Take on a role and do the hands-on work.',
        handler: { kind: 'route', to: '/actions', cta: 'Browse roles' },
      },
      {
        label: 'Complete your assigned tasks',
        state: 'soon',
        blurb: 'Finish the tasks already on your plate.',
        handler: { kind: 'route', to: '/actions', cta: 'Go to Action Center' },
      },
      {
        label: 'Submit KPI data',
        state: 'soon',
        blurb: "Report the numbers that show a project's impact.",
        handler: { kind: 'route', to: '/data', cta: 'Go to Data' },
      },
      {
        label: 'Visit a project',
        state: 'soon',
        blurb: 'Explore a project up close on the globe.',
        handler: { kind: 'route', to: '/discover', cta: 'Open Discover' },
      },
    ],
    lockedHint: 'Lead a project · Leader',
  },
  heart: {
    title: 'Heart',
    subtitle: 'Connection & compassion',
    actions: [
      {
        label: 'Support a teammate',
        state: 'soon',
        blurb: 'Help someone on your team move their work forward.',
        handler: { kind: 'route', to: '/actions', cta: 'Go to Action Center' },
      },
      {
        label: 'Log volunteer time',
        state: 'soon',
        blurb: "Record the time you've given.",
        handler: { kind: 'route', to: '/data', cta: 'Go to Data' },
      },
      {
        label: 'Back a campaign',
        state: 'soon',
        blurb: 'Lend your support to a community campaign.',
        handler: { kind: 'tray', tray: 'campaign', cta: 'Back a campaign' },
      },
      {
        label: 'Help a community beyond your own',
        state: 'soon',
        blurb: "Support a community you don't belong to.",
        handler: { kind: 'route', to: '/communities', cta: 'Explore communities' },
      },
    ],
    lockedHint: 'Rally a giving effort · Leader',
  },
  wings: {
    title: 'Wings',
    subtitle: 'Support & enablement',
    actions: [
      {
        label: 'Declare a skill you could share',
        state: 'soon',
        blurb: 'Tell others what you can help with.',
        handler: { kind: 'route', to: '/profile', cta: 'Go to your profile' },
      },
      {
        label: 'Tag your areas of expertise',
        state: 'soon',
        blurb: 'Mark the topics you know well so others can find you.',
        handler: { kind: 'route', to: '/profile', cta: 'Go to your profile' },
      },
      { label: 'Offer a resource', state: 'soon', note: 'opens later' },
    ],
  },
  roots: {
    title: 'Roots',
    subtitle: 'Commitment & steadfastness',
    actions: [
      {
        label: 'Attend a civic meeting in person',
        state: 'soon',
        note: 'unlocks Gold',
        blurb: 'Show up in person — the flagship act that anchors your real-world commitment. Attendance verification is coming soon.',
        handler: { kind: 'info' },
      },
      {
        label: 'Vouch for a teammate',
        state: 'soon',
        blurb: "Confirm a teammate's contribution from first-hand knowledge.",
        handler: { kind: 'route', to: '/actions', cta: 'Go to Action Center' },
      },
      { label: 'Enable two-factor auth', state: 'done' },
      { label: 'Accept the agreement', state: 'done' },
      { label: 'Complete your profile', state: 'done' },
    ],
  },
};
